import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpEvent, HttpHandler, HttpRequest} from '@angular/common/http';
import {Observable} from 'rxjs';
import {CheckRunScheduleControllerService} from '../../api/api/checkRunScheduleController.service';
import {Configuration} from '../../api/configuration';
import {CheckRunScheduleRequest} from '../../api/model/checkRunScheduleRequest';
import {CheckRunScheduleView} from '../../api/model/checkRunScheduleView';
import {SchedulePreviewView} from '../../api/model/schedulePreviewView';
import {SUPPRESS_ERROR_HANDLING} from '../../interceptors/http-error.interceptor';

/**
 * Leitet jede Anfrage an die normale Interceptor-Kette weiter, setzt vorher aber
 * {@link SUPPRESS_ERROR_HANDLING}. Die generierten Service-Methoden haben keinen Parameter für einen
 * `HttpContext`; so bleibt der generierte Code unverändert.
 */
export class QuietHttpHandler extends HttpHandler {
  constructor(private readonly next: HttpHandler) {
    super();
  }

  handle(req: HttpRequest<unknown>): Observable<HttpEvent<unknown>> {
    return this.next.handle(req.clone({context: req.context.set(SUPPRESS_ERROR_HANDLING, true)}));
  }
}

/**
 * Zugriff auf die Zeitpläne für Prüfläufe (`/workflows/check-run-schedules`) über den generierten
 * {@link CheckRunScheduleControllerService}.
 *
 * <p>Der Service bekommt einen eigenen `HttpClient`, dessen Anfragen der globale
 * `HttpErrorInterceptor` nicht meldet: 404/409/422 sind erwartete Zustände, die Popup, Tab und
 * Karte selbst anzeigen. Ohne das gäbe es jede Meldung doppelt. Lade-Interceptor und Dedup laufen
 * unverändert mit.
 *
 * <p>Die Basis-URL kommt aus der regulär injizierten Instanz, so wie sie die Generierung für die
 * jeweilige Umgebung festlegt (Muster `rule-test.api.ts`).
 */
@Injectable({providedIn: 'root'})
export class CheckRunScheduleApi {
  private readonly client: CheckRunScheduleControllerService;

  constructor() {
    const quietHttp = new HttpClient(new QuietHttpHandler(inject(HttpHandler)));
    const basePath: string = (inject(CheckRunScheduleControllerService) as any).basePath;
    const configuration = inject(Configuration, {optional: true}) ?? undefined;
    this.client = new CheckRunScheduleControllerService(quietHttp, basePath, configuration as Configuration);
  }

  list(projectId: number): Observable<CheckRunScheduleView[]> {
    return this.client.listCheckRunSchedules(projectId);
  }

  create(projectId: number, request: CheckRunScheduleRequest): Observable<CheckRunScheduleView> {
    return this.client.createCheckRunSchedule(request, projectId);
  }

  update(projectId: number, scheduleId: string, request: CheckRunScheduleRequest): Observable<CheckRunScheduleView> {
    return this.client.updateCheckRunSchedule(request, projectId, scheduleId);
  }

  pause(projectId: number, scheduleId: string): Observable<CheckRunScheduleView> {
    return this.client.pauseCheckRunSchedule(projectId, scheduleId);
  }

  resume(projectId: number, scheduleId: string): Observable<CheckRunScheduleView> {
    return this.client.resumeCheckRunSchedule(projectId, scheduleId);
  }

  delete(projectId: number, scheduleId: string): Observable<unknown> {
    return this.client.deleteCheckRunSchedule(projectId, scheduleId);
  }

  preview(projectId: number, request: CheckRunScheduleRequest): Observable<SchedulePreviewView> {
    return this.client.previewCheckRunSchedule(request, projectId);
  }
}
